import { useEffect, useState } from 'react';
import { TournamentWidgetView } from '../../../../components/Widgets/TournamentView';
import CheckboxCustomIcon from '../../../../components/ui/CheckBoxCustom';
import Button from '../../../../components/ui/button';
import { sentryCapture } from '../../../../helpers/utils';
import { useApi } from '../../../../hooks/useApi';
import { useNavigateStore } from '../../../../store/navigation';
import { useTokenStore } from '../../../../store/token';
import { SentryError } from '../../../../types/enums';
import { TournamentData } from '../../../../types/types';

interface iProps {
	tabsSelect: string;
	filter: any;
	onChangeFilter: (type: string, status: string) => void;
}

export const AllTournament = ({
	tabsSelect,
	filter,
	onChangeFilter,
}: iProps) => {
	const api = useApi();
	const token = useTokenStore((state: any) => state.token);
	const { resetNavigate, goBackButton } = useNavigateStore();
	const navigateState = useNavigateStore((state: any) => state.navigateState);
	const cameFromBackButton = useNavigateStore(
		(state: any) => state.cameFromBackButton
	);

	const pageSize = 3;
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState<TournamentData[]>([]);
	const [page, setPage] = useState(0);
	const [totalPage, setTotalPage] = useState(0);
	const [hasMore, setHasMore] = useState(true);
	const [isFirstReq, setIsFirstReq] = useState(true);

	useEffect(() => {
		if (cameFromBackButton && !navigateState) {
			goBackButton(false);
			resetNavigate();
		}
		if (
			cameFromBackButton &&
			navigateState &&
			navigateState.tabsSelect === 'Todos'
		) {
			if (!navigateState.filter) {
				goBackButton(false);
				resetNavigate();
				return;
			}
			setData(navigateState.data);
			setPage(navigateState.page);
			setTotalPage(navigateState.totalPage);
			setHasMore(navigateState.hasMore);
			setTimeout(() => {
				const goToElement = document.getElementById(
					navigateState.tournamentId
				);
				if (goToElement) {
					goToElement.scrollIntoView({
						behavior: 'auto',
					});
				} else {
					console.error(
						'Elemento não encontrado:',
						navigateState.tournamentId
					);
				}
			}, 0);
			goBackButton(false);
			resetNavigate();
		}
	}, []);

	useEffect(() => {
		if (!cameFromBackButton) {
			if (loading) return;
			if (tabsSelect === 'Todos') {
				fetchTournament(page);
			}
		}
	}, [page, filter.open, filter.finish]);

	useEffect(() => {
		if (!cameFromBackButton) {
			if (
				!loading &&
				tabsSelect === 'Todos' &&
				data.length === 0 &&
				!isFirstReq
			) {
				fetchTournament(page);
			}
		}
	}, [tabsSelect]);

	useEffect(() => {
		if (!cameFromBackButton) {
			if (loading) return;

			setHasMore(true);
			setData([]);
			setPage(0);
		}
	}, [filter.open, filter.finish]);

	const fetchTournament = async (page: number) => {
		setLoading(true);

		try {
			if (totalPage !== 0 && page >= totalPage) {
				setHasMore(false);
				return;
			}
			const { open, finish } = filter;
			const res = await api.getAllTournament(
				{ pageSize, offset: page * pageSize, open, finish },
				token
			);
			if (!res?.success) {
				throw new Error(res.message);
			}
			setTotalPage(res.totalPages);
			setData((prev: any) => [...prev, ...res.data]);
			if (res.totalPages <= 1) {
				setHasMore(false);
			} else {
				setHasMore(true);
			}
			setIsFirstReq(false);
		} catch (error: any) {
			sentryCapture(error, 'getTournament', SentryError.Error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex flex-col gap-6 mb-6">
			<div className="flex flex-row gap-4">
				<div className="h-5 flex-row justify-start items-start inline-flex">
					<div className="flex flex-row items-center gap-2">
						<CheckboxCustomIcon
							checked={filter.open === 'Inscrições abertas'}
							onCheckedChange={() => {
								if (loading) return;
								if (filter.open === '') {
									onChangeFilter(
										'open',
										'Inscrições abertas'
									);
								} else {
									onChangeFilter('open', '');
								}
							}}
						/>
						<div
							onClick={() => {
								if (loading) return;
								if (filter.open === '') {
									onChangeFilter(
										'open',
										'Inscrições abertas'
									);
								} else {
									onChangeFilter('open', '');
								}
							}}
							className="text-custom-gray10 text-sm font-normal font-bahnschrift leading-tight cursor-pointer"
						>
							Inscrições abertas
						</div>
					</div>
				</div>
				<div className="h-5 flex-row justify-start items-start inline-flex">
					<div className="flex flex-row items-center gap-2">
						<CheckboxCustomIcon
							checked={filter.finish === 'Em Apuração'}
							onCheckedChange={() => {
								if (loading) return;
								if (filter.finish === '') {
									onChangeFilter('closed', 'Em Apuração');
								} else {
									onChangeFilter('closed', '');
								}
							}}
						/>
						<div
							onClick={() => {
								if (loading) return;
								if (filter.finish === '') {
									onChangeFilter('closed', 'Em Apuração');
								} else {
									onChangeFilter('closed', '');
								}
							}}
							className="text-custom-gray10 text-sm font-normal font-bahnschrift leading-tight cursor-pointer"
						>
							Finalizados
						</div>
					</div>
				</div>
			</div>

			{data &&
				data.map((tournament, index) => (
					<div
						key={index}
						id={tournament.id}
						className="flex flex-col gap-2"
					>
						<TournamentWidgetView
							key={index}
							data={tournament}
							navigationData={{
								tournamentId: tournament.id,
								data,
								tabsSelect,
								page,
								totalPage,
								hasMore,
								filter,
							}}
						/>
					</div>
				))}

			{hasMore && (
				<Button
					isLoading={loading}
					onClick={() => {
						setPage(page + 1);
					}}
					className="w-full px-3.5 py-3.5"
				>
					Carregar mais
				</Button>
			)}
		</div>
	);
};
